import { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button } from "@direct-campaign/ui";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { apiFetch } from "../../lib/api";

interface Tag {
  id: string;
  label: string;
  code: string;
  color: string | null;
}

interface ResponseDraft {
  responseText: string;
  tagId: string | null;
  // Stable local key of the question to jump to; null = continue in order.
  // Keys survive question reorders, unlike array indices.
  nextQuestionKey: string | null;
}

interface QuestionDraft {
  key: string;
  questionText: string;
  required: boolean;
  responses: ResponseDraft[];
}

interface SurveyDetail {
  id: string;
  title: string;
  description: string | null;
  isActive: boolean;
  questions: {
    id: string;
    questionText: string;
    required: boolean;
    responses: { responseText: string; tagId: string | null; nextQuestionId: string | null }[];
  }[];
}

const inputClass =
  "flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring";

function emptyResponse(): ResponseDraft {
  return { responseText: "", tagId: null, nextQuestionKey: null };
}

function emptyQuestion(): QuestionDraft {
  return {
    key: crypto.randomUUID(),
    questionText: "",
    required: false,
    responses: [emptyResponse()],
  };
}

export function SurveyBuilderPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [questions, setQuestions] = useState<QuestionDraft[]>([emptyQuestion()]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    async function load() {
      try {
        const tagsRes = await apiFetch("/api/tags");
        if (tagsRes.ok) {
          const data = (await tagsRes.json()) as { tags?: Tag[] };
          setTags(data.tags ?? []);
        }

        if (id) {
          const res = await apiFetch(`/api/surveys/${id}`);
          if (!res.ok) {
            setError("Failed to load survey.");
            return;
          }
          const data = (await res.json()) as { survey?: SurveyDetail };
          const survey = data.survey;
          if (!survey) {
            setError("Failed to load survey.");
            return;
          }
          setTitle(survey.title);
          setDescription(survey.description ?? "");
          setIsActive(survey.isActive);
          // Server question IDs double as the stable local keys, so saved
          // routing targets (nextQuestionId) translate directly.
          setQuestions(
            survey.questions.length > 0
              ? survey.questions.map((q) => ({
                  key: q.id,
                  questionText: q.questionText,
                  required: q.required,
                  responses:
                    q.responses.length > 0
                      ? q.responses.map((r) => ({
                          responseText: r.responseText,
                          tagId: r.tagId,
                          nextQuestionKey: r.nextQuestionId,
                        }))
                      : [emptyResponse()],
                }))
              : [emptyQuestion()],
          );
        }
      } catch {
        setError(isEdit ? "Failed to load survey." : "Failed to load tags.");
      } finally {
        setLoading(false);
      }
    }
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  function updateQuestion(qIndex: number, patch: Partial<QuestionDraft>) {
    setQuestions((prev) => prev.map((q, i) => (i === qIndex ? { ...q, ...patch } : q)));
  }

  function moveQuestion(qIndex: number, direction: -1 | 1) {
    setQuestions((prev) => {
      const target = qIndex + direction;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(qIndex, 1);
      if (moved) next.splice(target, 0, moved);
      return next;
    });
  }

  function removeQuestion(qIndex: number) {
    setQuestions((prev) => prev.filter((_, i) => i !== qIndex));
  }

  function updateResponse(qIndex: number, rIndex: number, patch: Partial<ResponseDraft>) {
    setQuestions((prev) =>
      prev.map((q, i) =>
        i === qIndex
          ? {
              ...q,
              responses: q.responses.map((r, j) => (j === rIndex ? { ...r, ...patch } : r)),
            }
          : q,
      ),
    );
  }

  function addResponse(qIndex: number) {
    setQuestions((prev) =>
      prev.map((q, i) =>
        i === qIndex ? { ...q, responses: [...q.responses, emptyResponse()] } : q,
      ),
    );
  }

  function removeResponse(qIndex: number, rIndex: number) {
    setQuestions((prev) =>
      prev.map((q, i) =>
        i === qIndex ? { ...q, responses: q.responses.filter((_, j) => j !== rIndex) } : q,
      ),
    );
  }

  function validate(): string | null {
    if (!title.trim()) return "Survey title is required.";
    if (questions.length === 0) return "Add at least one question.";
    for (const [i, q] of questions.entries()) {
      if (!q.questionText.trim()) return `Question ${i + 1} needs text.`;
      for (const [j, r] of q.responses.entries()) {
        if (!r.responseText.trim())
          return `Response ${j + 1} on question ${i + 1} needs text (or remove it).`;
      }
    }
    return null;
  }

  async function handleSave() {
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }
    setError("");
    setSaving(true);

    // Routing targets are stored as stable keys locally; the API wants the
    // 0-based index of the target question. A target that was deleted resolves
    // to null ("continue in order").
    const keyToIndex = new Map(questions.map((q, i) => [q.key, i]));

    const body = JSON.stringify({
      title: title.trim(),
      description: description.trim() || null,
      isActive,
      questions: questions.map((q, qIndex) => ({
        questionText: q.questionText.trim(),
        required: q.required,
        responses: q.responses.map((r) => {
          const target = r.nextQuestionKey !== null ? keyToIndex.get(r.nextQuestionKey) : undefined;
          return {
            responseText: r.responseText.trim(),
            tagId: r.tagId,
            nextQuestionIndex: target !== undefined && target !== qIndex ? target : null,
          };
        }),
      })),
    });

    const res = id
      ? await apiFetch(`/api/surveys/${id}`, { method: "PUT", body })
      : await apiFetch("/api/surveys", { method: "POST", body });

    setSaving(false);

    if (res.ok) {
      void navigate("/dashboard/surveys");
    } else {
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      setError(data.error ?? "Failed to save survey.");
    }
  }

  if (loading) {
    return <p className="text-muted-foreground">Loading…</p>;
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">
            {isEdit ? "Edit survey" : "New survey"}
          </h2>
          <p className="text-muted-foreground mt-1">
            Questions appear to canvassers in order. Map responses to tags to
            auto-tag voters as answers come in.
          </p>
        </div>
        <Button variant="ghost" asChild>
          <Link to="/dashboard/surveys">Back to surveys</Link>
        </Button>
      </div>

      {error && (
        <div className="rounded-md bg-destructive/10 border border-destructive/20 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* Survey metadata */}
      <div className="rounded-lg border bg-card p-5 space-y-4">
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Title</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className={inputClass}
            placeholder="Door-knock support survey"
          />
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Description (optional)</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            placeholder="Shown to canvassers as context before they start."
          />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            className="h-4 w-4 rounded border-input"
          />
          Active — available to attach to walkbooks
        </label>
      </div>

      {/* Questions */}
      <div className="space-y-4">
        {questions.map((q, qIndex) => (
          <div key={q.key} className="rounded-lg border bg-card p-5 space-y-4">
            <div className="flex items-start gap-3">
              <span className="mt-2 text-xs font-semibold text-muted-foreground shrink-0 w-6">
                Q{qIndex + 1}
              </span>
              <div className="flex-1 space-y-3">
                <input
                  type="text"
                  value={q.questionText}
                  onChange={(e) => updateQuestion(qIndex, { questionText: e.target.value })}
                  className={inputClass}
                  placeholder="Do you plan to vote in the upcoming election?"
                />
                <label className="flex items-center gap-2 text-sm text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={q.required}
                    onChange={(e) => updateQuestion(qIndex, { required: e.target.checked })}
                    className="h-4 w-4 rounded border-input"
                  />
                  Required — canvassers must answer before moving on
                </label>
              </div>
              <div className="flex items-center gap-0.5 shrink-0">
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={qIndex === 0}
                  onClick={() => moveQuestion(qIndex, -1)}
                  title="Move up"
                >
                  <ArrowUp className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={qIndex === questions.length - 1}
                  onClick={() => moveQuestion(qIndex, 1)}
                  title="Move down"
                >
                  <ArrowDown className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-destructive hover:text-destructive"
                  disabled={questions.length === 1}
                  onClick={() => removeQuestion(qIndex)}
                  title="Remove question"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Responses */}
            <div className="ml-9 space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Response options
              </p>
              {q.responses.map((r, rIndex) => (
                <div key={rIndex} className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={r.responseText}
                      onChange={(e) =>
                        updateResponse(qIndex, rIndex, { responseText: e.target.value })
                      }
                      className={inputClass}
                      placeholder={`Option ${rIndex + 1} — e.g. "Strongly support"`}
                    />
                    <select
                      value={r.tagId ?? ""}
                      onChange={(e) =>
                        updateResponse(qIndex, rIndex, { tagId: e.target.value || null })
                      }
                      className="flex h-9 w-44 shrink-0 rounded-md border border-input bg-background px-2 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                      title="Tag applied to the voter when this response is chosen"
                    >
                      <option value="">No tag</option>
                      {tags.map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.label} ({t.code})
                        </option>
                      ))}
                    </select>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-destructive hover:text-destructive shrink-0"
                      disabled={q.responses.length === 1}
                      onClick={() => removeResponse(qIndex, rIndex)}
                      title="Remove option"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  {questions.length > 1 && (
                    <div className="flex items-center gap-2 pl-1">
                      <span className="text-xs text-muted-foreground shrink-0">
                        After this answer:
                      </span>
                      <select
                        value={r.nextQuestionKey ?? ""}
                        onChange={(e) =>
                          updateResponse(qIndex, rIndex, {
                            nextQuestionKey: e.target.value || null,
                          })
                        }
                        className="h-7 rounded-md border border-input bg-background px-2 text-xs shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                        title="Which question the canvasser sees next when this response is chosen"
                      >
                        <option value="">Continue in order</option>
                        {questions.map((target, tIndex) =>
                          tIndex === qIndex ? null : (
                            <option key={target.key} value={target.key}>
                              Go to Q{tIndex + 1}
                              {target.questionText.trim()
                                ? ` — ${target.questionText.trim().slice(0, 40)}`
                                : ""}
                            </option>
                          ),
                        )}
                      </select>
                    </div>
                  )}
                </div>
              ))}
              <Button size="sm" variant="ghost" onClick={() => addResponse(qIndex)}>
                <Plus className="w-4 h-4 mr-1" /> Add option
              </Button>
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2">
        <Button variant="outline" onClick={() => setQuestions((prev) => [...prev, emptyQuestion()])}>
          <Plus className="w-4 h-4 mr-1" /> Add question
        </Button>
        <div className="flex-1" />
        <Button variant="ghost" asChild>
          <Link to="/dashboard/surveys">Cancel</Link>
        </Button>
        <Button disabled={saving} onClick={() => void handleSave()}>
          {saving ? "Saving…" : isEdit ? "Save changes" : "Create survey"}
        </Button>
      </div>

      {tags.length === 0 && (
        <p className="text-sm text-muted-foreground">
          Tip: create <Link to="/dashboard/tags" className="underline">tags</Link> first to
          auto-tag voters based on their answers.
        </p>
      )}
    </div>
  );
}
